import * as React from "react"
import PropTypes from "prop-types"
import styled from "@emotion/styled"
import Image from "components/Image"
import UnstyledLink from "components/UnstyledLink"
import HeroScrollCaseDemoMobile from "components/Hero/HeroScrollCaseDemoMobile"
import HeroScrollPcDemoDesktop from "components/Hero/HeroScrollPcDemoDesktop"

const CardWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  min-height: 380px;
  width: 100%;
  border-radius: 20px;
  overflow: hidden;
  transition:
    transform 0.3s ease-in-out,
    box-shadow 0.3s ease-in-out;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  align-self: stretch;
  margin: 0;
  cursor: pointer;

  background-color: ${({ theme }) => theme.palette.primary.main};

  &:hover {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    width: 100%;
    margin: 0;
  }
`

const BackgroundImageWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
  }

  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.1) 0%,
      rgba(0, 0, 0, 0.2) 50%,
      rgba(0, 0, 0, 0.7) 100%
    );
    z-index: 1;
  }
`

const BusinessLogoWrapper = styled.div`
  position: relative;
  z-index: 2;
  height: 60px;
  max-height: 60px;
  width: auto;
  display: flex;
  align-items: center;
  justify-content: start;
  background-color: transparent;
  border-radius: 8px;
  padding: 24px;

  img {
    max-height: 60px;
    width: auto;
    object-fit: contain;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15));
  }
`

const ContentWrapper = styled.div`
  position: relative;
  z-index: 5;
  margin-top: auto;
  padding: 32px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  background: linear-gradient(to bottom, #00000000 0%, #00000066 100%);

  ${({ theme }) => theme.breakpoints.down("md")} {
    flex-wrap: wrap-reverse;
    justify-content: end;
    margin-top: 50%;
  }
`

const TitleWrapper = styled.div`
  flex: 1;
  ${({ theme }) => theme.breakpoints.down("md")} {
    margin-top: 24px;
    min-width: 100%;
  }
`

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  font-family: Gilroy;
  margin: 0;
  color: #ffffff;
  max-width: 65%;
  line-height: 100%;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  overflow-wrap: break-word;
  word-break: break-word;
  letter-spacing: 0%;
`

const HeroWrapper = styled.div`
  position: relative;
  display: var(--hero-wrapper-display, flex);
  align-items: flex-end;
  z-index: 6;
  pointer-events: none;

  ${({ theme }) => theme.breakpoints.down("xl")} {
    transform: scale(1.2);
    transform-origin: bottom right;
    margin-top: -20px;
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    transform: scale(1.3);
    transform-origin: bottom right;
    margin-top: -20px;
  }

  ${({ theme }) => theme.breakpoints.down("xsm")} {
    transform: scale(1);
    transform-origin: bottom right;
    margin-top: -20px;
  }
`

const MobileDeviceWrapper = styled(HeroScrollCaseDemoMobile)`
  box-shadow: 0px 4px 15px -8px rgba(0, 0, 0, 0.7);
  max-width: 100%;
  width: auto;
  height: auto;
  max-height: 135px;
  border: 2px solid #ffffff99;
  border-radius: 8px;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  perspective: 1000;
  -webkit-perspective: 1000;

  img {
    max-width: 100%;
    height: auto;
    max-height: 160px;
    object-fit: contain;
  }
`

const DesktopDeviceWrapper = styled(HeroScrollPcDemoDesktop)`
  box-shadow: 0px 8px 20px -10px rgba(0, 0, 0, 0.7);
  max-width: 100%;
  width: 220px;
  height: auto;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-radius: 8px;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  perspective: 1000;
  -webkit-perspective: 1000;
  transform-style: preserve-3d;
  -webkit-transform-style: preserve-3d;
  box-sizing: border-box;

  img {
    max-width: 100%;
    height: auto;
    display: block;
  }
`

const DesktopMobileContainer = styled.div`
  position: relative;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform-style: preserve-3d;
  -webkit-transform-style: preserve-3d;
  box-sizing: border-box;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;

  ${DesktopDeviceWrapper} {
    margin-left: 25px;
    margin-bottom: 10px;
    position: relative;
    top: 0;
  }

  ${MobileDeviceWrapper} {
    position: absolute;
    left: -10px;
    bottom: -20px;
    transform: scale(0.7) translate3d(0, 0, 0);
  }
`

const MobileMobileContainer = styled.div`
  position: relative;

  /* Prevent sub-pixel rendering issues */
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
`

const PrimaryMobileWrapper = styled.div`
  position: relative;
  z-index: 2;
  margin-right: 50px;

  ${MobileDeviceWrapper} {
    box-shadow: 0px 4px 15px -8px rgba(0, 0, 0, 0.7);
  }
`

const SecondaryMobileWrapper = styled.div`
  position: absolute;
  z-index: 1;
  bottom: 0;
  right: 0;
  transform: translateY(2.5%);
  scale: 0.95;

  ${MobileDeviceWrapper} {
    box-shadow: 0px 4px 15px -8px rgba(0, 0, 0, 0.5);
  }
`

const CardOverlayLink = styled(UnstyledLink)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  cursor: pointer;
  outline: none;

  &:focus-visible {
    box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.4);
    border-radius: 18px;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
`

const CaseStudyCard = ({
  id,
  title,
  titleTag,
  buttonLink,
  backgroundImage,
  buisnessLogoImage,
  desktopImage,
  mobileImage,
  type,
  className,
}) => (
  <CardWrapper key={id} className={className}>
    <CardOverlayLink to={buttonLink} ariaLabel={`Explore more ${title}`} tabIndex="-1" />

    {backgroundImage && (
      <BackgroundImageWrapper>
        <Image
          {...backgroundImage}
          alt={title}
          objectFit="cover"
          objectPosition="center center"
          style={{ width: "100%", height: "100%" }}
        />
      </BackgroundImageWrapper>
    )}

    {buisnessLogoImage && (
      <BusinessLogoWrapper>
        <Image
          {...buisnessLogoImage}
          alt={`${title} logo`}
          objectFit="contain"
          style={{ maxHeight: "60px", width: "auto" }}
        />
      </BusinessLogoWrapper>
    )}

    <ContentWrapper>
      <TitleWrapper>
        <CardTitle as={titleTag}>
          <UnstyledLink
            ariaLabel={`Explore more ${title}`}
            to={buttonLink}
            style={{ position: "relative", zIndex: 15 }}
          >
            {title}
          </UnstyledLink>
        </CardTitle>
      </TitleWrapper>

      <HeroWrapper>
        {type === "Desktop" && desktopImage && (
          <DesktopDeviceWrapper>
            <Image {...desktopImage} alt={`${title} desktop showcase`} />
          </DesktopDeviceWrapper>
        )}

        {type === "Mobile" && mobileImage && (
          <MobileDeviceWrapper>
            <Image {...mobileImage} alt={`${title} mobile showcase`} />
          </MobileDeviceWrapper>
        )}

        {type === "Desktop-mobile" && desktopImage && mobileImage && (
          <DesktopMobileContainer>
            <DesktopDeviceWrapper>
              <Image {...desktopImage} alt={`${title} desktop showcase`} />
            </DesktopDeviceWrapper>
            <MobileDeviceWrapper style={{}}>
              <Image {...mobileImage} alt={`${title} mobile showcase`} />
            </MobileDeviceWrapper>
          </DesktopMobileContainer>
        )}

        {type === "Mobile-mobile" && mobileImage && (
          <MobileMobileContainer>
            <PrimaryMobileWrapper>
              <MobileDeviceWrapper>
                <Image {...mobileImage} alt={`${title} mobile showcase (primary)`} />
              </MobileDeviceWrapper>
            </PrimaryMobileWrapper>
            <SecondaryMobileWrapper>
              <MobileDeviceWrapper>
                <Image
                  {...mobileImage}
                  objectFit="cover"
                  alt={`${title} mobile showcase (secondary)`}
                />
              </MobileDeviceWrapper>
            </SecondaryMobileWrapper>
          </MobileMobileContainer>
        )}
      </HeroWrapper>
    </ContentWrapper>
  </CardWrapper>
)

CaseStudyCard.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  titleTag: PropTypes.oneOf(["h1", "h2", "h3", "h4", "h5", "h6"]).isRequired,
  buttonLink: PropTypes.string.isRequired,
  backgroundImage: PropTypes.object,
  buisnessLogoImage: PropTypes.object,
  desktopImage: PropTypes.object,
  mobileImage: PropTypes.object,
  type: PropTypes.string.isRequired,
  className: PropTypes.string,
}

CaseStudyCard.defaultProps = {
  backgroundImage: null,
  buisnessLogoImage: null,
  desktopImage: null,
  mobileImage: null,
  className: "",
}

export default CaseStudyCard
